import { Box, Fade } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import type { TFunction } from 'i18next';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { clearSessionReturnPath, readSessionReturnPath } from '../../../api';
import { InlineAlert } from '../../../core-components/theme/components/InlineAlert';
import { fillColumnSx } from '../../../core-components/theme/components/Screen';
import { languageChoiceOf } from '../../../i18n/languages';
import { returnPathOf } from '../domain/returnPath';
import type { Refusal } from '../domain/refusal';
import { useCodeStepHistory } from '../hooks/logic/useCodeStepHistory';
import { useLoginLogic } from '../hooks/logic/useLoginLogic';
import type { IdentifierView, LoginBusy, LoginLogic, LoginNoticeView, LoginView } from '../hooks/logic/useLoginLogic';
import type { OpenedSession } from '../types/domain.types';

import { SessionChecking } from '../../../session';

import { AuthShell } from './AuthShell';
import { identifierHelp } from './identifierText';
import { IdentifyPanel } from './IdentifyPanel';
import type { IdentifierFieldState } from './IdentifyPanel';
import { LanguageToggle } from './LanguageToggle';
import { LockedPanel } from './LockedPanel';
import { LoginCodePanel } from './LoginCodePanel';
import { LoginNotice } from './LoginNotice';
import { NoPasswordPanel } from './NoPasswordPanel';
import { NewPasswordStep } from './password/NewPasswordStep';
import { PasswordPanel } from './PasswordPanel';
import { codeOnlyText, passwordDetailText, refusalText } from './refusalText';

const AFTER_LOGIN_PATH = '/';

const LANGUAGE_TOOLBAR = <LanguageToggle />;

/** The login's own look while the server says whether someone is already signed in. */
export const PasscodeLoginPending: React.FC = () => (
  <AuthShell toolbar={LANGUAGE_TOOLBAR}>
    <SessionChecking placement="content" />
  </AuthShell>
);

const stepSx: SystemStyleObject<Theme> = {
  ...fillColumnSx,
  '@media (prefers-reduced-motion: reduce)': { transition: 'none !important' },
};

const passwordErrorOf = (t: TFunction, refusal: Refusal | null, missing: boolean): string | null => {
  if (missing) return t('passcodeAuth.password.required', 'Enter your password.');
  if (refusal === null) return null;
  const detail = passwordDetailText(t, refusal);
  const message = refusalText(t, refusal);
  return detail === null ? message : `${message} ${detail}`;
};

const fieldStateOf = (t: TFunction, field: IdentifierView, onChange: (value: string) => void, showUnverified: boolean): IdentifierFieldState => {
  const help = identifierHelp(t, {
    kind: field.kind,
    refusal: field.refusal,
    problem: field.problem,
    unverifiedKind: showUnverified ? field.unverifiedKind : null,
    codeLength: field.codeLength,
  });
  return { value: field.value, kind: field.kind, helperText: help.text, error: help.error, onChange };
};

const sendingChannelOf = (busy: LoginBusy) => (busy.kind === 'sending' ? busy.channel : null);

interface NoticeProps {
  readonly notice: LoginNoticeView;
  readonly retryInline: boolean;
  readonly actions: LoginLogic['actions'];
}

const Notice: React.FC<NoticeProps> = ({ notice, retryInline, actions }) => (
  <LoginNotice
    waiting={notice.waiting}
    hold={notice.hold}
    problem={notice.problem}
    retryInline={retryInline}
    passwordOffered={notice.passwordOffered}
    onRetry={actions.retry}
  />
);

interface StepPanelProps {
  readonly logic: LoginLogic;
  readonly title: string;
  readonly subtitle?: React.ReactNode;
  readonly onChangeIdentifier: () => void;
}

const StepPanel: React.FC<StepPanelProps> = ({ logic, title, subtitle, onChangeIdentifier }) => {
  const { t } = useTranslation();
  const { view, busy, actions } = logic;
  const sending = busy.kind === 'sending';
  const sendingChannel = sendingChannelOf(busy);

  switch (view.screen) {
    case 'code':
      return (
        <LoginCodePanel
          session={view.session}
          status={view.status}
          resending={sendingChannel === view.session.sent.sentOn}
          resendProblem={view.resendProblem}
          waiting={view.waiting}
          onCodeChange={actions.setCode}
          onComplete={actions.verify}
          onResend={actions.resend}
          onChangeIdentifier={onChangeIdentifier}
        />
      );
    case 'locked':
      return (
        <LockedPanel lockUntil={view.lockUntil} lockMinutes={view.lockMinutes} sending={sending} onBack={actions.back} onSendNew={actions.sendNewCode} />
      );
    case 'newPassword':
      return <NewPasswordStep grant={view.grant} onFinished={actions.finish} />;
    case 'done':
      return <InlineAlert severity="success" role="status" message={t('passcodeAuth.done.message', 'Signed in. Taking you in…')} />;
    case 'password':
      return (
        <PasswordPanel
          title={title}
          subtitle={subtitle}
          field={fieldStateOf(t, view.field, actions.setIdentifier, false)}
          fieldProblemKey={view.field.problemKey}
          password={view.password}
          passwordError={passwordErrorOf(t, view.passwordRefusal, view.passwordMissing)}
          passwordProblemKey={view.passwordProblemKey}
          loggingIn={busy.kind === 'loggingIn'}
          sending={sending}
          held={view.held}
          notice={<Notice notice={view.notice} retryInline actions={actions} />}
          onPasswordChange={actions.setPassword}
          onLogIn={actions.logIn}
          onForgot={actions.forgotPassword}
        />
      );
    case 'noPassword':
      return (
        <NoPasswordPanel
          title={title}
          message={codeOnlyText(t, view.reason)}
          subtitle={subtitle}
          field={fieldStateOf(t, view.field, actions.setIdentifier, false)}
          fieldProblemKey={view.field.problemKey}
          codeChoices={view.codeChoices}
          sendingChannel={sendingChannel}
          sending={sending}
          held={view.held}
          notice={<Notice notice={view.notice} retryInline actions={actions} />}
          onSend={actions.send}
          onBack={actions.back}
        />
      );
    case 'identify':
      return (
        <IdentifyPanel
          title={title}
          subtitle={subtitle}
          field={fieldStateOf(t, view.field, actions.setIdentifier, true)}
          fieldProblemKey={view.field.problemKey}
          codeChoices={view.codeChoices}
          sendingChannel={sendingChannel}
          sending={sending}
          checkingPassword={busy.kind === 'lookingUp'}
          retrying={view.retrying}
          held={view.held}
          notice={<Notice notice={view.notice} retryInline={!view.retrying} actions={actions} />}
          onSend={actions.send}
          onRetry={actions.retry}
          onUsePassword={actions.choosePassword}
        />
      );
  }
};

const screenKeyOf = (view: LoginView): string => view.screen;

interface LoginStepsProps {
  readonly title: string;
  readonly subtitle?: React.ReactNode;
  readonly onVerified: (session: OpenedSession) => void;
}

const LoginSteps: React.FC<LoginStepsProps> = ({ title, subtitle, onVerified }) => {
  const theme = useTheme();
  const history = useCodeStepHistory();
  const logic = useLoginLogic({ onVerified, onCodeSent: history.openCode, showCode: history.atCode });
  const changeIdentifier = (): void => {
    logic.actions.back();
    history.leaveCode();
  };
  return (
    <Fade key={screenKeyOf(logic.view)} in appear timeout={theme.transitions.duration.shorter}>
      <Box sx={stepSx}>
        <StepPanel logic={logic} title={title} subtitle={subtitle} onChangeIdentifier={changeIdentifier} />
      </Box>
    </Fade>
  );
};

export interface PasscodeLoginScreenProps {
  onAuthenticated?: () => void;
  title?: string;
  subtitle?: React.ReactNode;
}

export const PasscodeLoginScreen: React.FC<PasscodeLoginScreenProps> = ({ onAuthenticated, title, subtitle }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const shownTitle = title ?? t('passcodeAuth.login.title', 'Log in');

  const done = (session: OpenedSession): void => {
    if (session.language !== null && session.language !== languageChoiceOf(i18n.language)) void i18n.changeLanguage(session.language);
    if (onAuthenticated) {
      onAuthenticated();
      return;
    }
    const landing = returnPathOf(location.state) ?? AFTER_LOGIN_PATH;
    navigate(readSessionReturnPath() ?? landing, { replace: true });
    clearSessionReturnPath();
  };

  return (
    <AuthShell toolbar={LANGUAGE_TOOLBAR}>
      <LoginSteps title={shownTitle} subtitle={subtitle} onVerified={done} />
    </AuthShell>
  );
};
